import {isFeatureEnabled} from 'feature-flags';

import {ForwardedRef, forwardRef, ReactElement} from 'react';

import {featureFlags} from '@dms/feature-flags';

import {
  DataGrid,
  DataGridProps,
  DataGridRef,
  type ActionCallback as DataGridActionCallback,
} from 'features/datagrid';
import {EagGrid, EagGridProps} from 'features/eag-grid';

type DataGridEscapeHatchProps = Pick<
  DataGridProps,
  | '_useAsLastResort_definitionBehaviorOverrides'
  | '_useAsLastResort_colDefOverrides'
  | '_useAsLastResort_connectionOverride'
  | '_useAsLastResort_linkClickOverride'
>;

export type DmsDataGridProps<T extends readonly string[] = readonly string[]> = Omit<
  EagGridProps<T>,
  'predefinedColumns'
> & {
  predefinedColumns?: T;
} & DataGridEscapeHatchProps;

const EMPTY_COLUMNS: readonly string[] = [];

function isDefinedColumns<T extends readonly string[]>(columns: T | undefined): columns is T {
  return columns !== undefined;
}

function isDataGridCallback(
  callback: unknown
): callback is DataGridActionCallback<readonly string[]> {
  return typeof callback === 'function';
}

function DmsDataGridInner<T extends readonly string[] = readonly string[]>(
  props: DmsDataGridProps<T>,
  ref: ForwardedRef<DataGridRef>
): ReactElement {
  const {
    _useAsLastResort_definitionBehaviorOverrides,
    _useAsLastResort_colDefOverrides,
    _useAsLastResort_connectionOverride,
    _useAsLastResort_linkClickOverride,
    predefinedColumns,
    ...rest
  } = props;

  const resolvedColumns = isDefinedColumns(predefinedColumns) ? predefinedColumns : EMPTY_COLUMNS;

  if (isFeatureEnabled(featureFlags.EAG_GRID)) {
    return <EagGrid predefinedColumns={resolvedColumns} {...rest} />;
  }

  const {actionCallback, ...restWithoutCallback} = rest;

  return (
    <DataGrid
      ref={ref}
      _useAsLastResort_definitionBehaviorOverrides={_useAsLastResort_definitionBehaviorOverrides}
      _useAsLastResort_colDefOverrides={_useAsLastResort_colDefOverrides}
      _useAsLastResort_connectionOverride={_useAsLastResort_connectionOverride}
      _useAsLastResort_linkClickOverride={_useAsLastResort_linkClickOverride}
      predefinedColumns={resolvedColumns}
      actionCallback={isDataGridCallback(actionCallback) ? actionCallback : undefined}
      {...restWithoutCallback}
    />
  );
}

export const DmsDataGrid = forwardRef(DmsDataGridInner) as <
  T extends readonly string[] = readonly string[],
>(
  props: DmsDataGridProps<T> & {ref?: ForwardedRef<DataGridRef>}
) => ReactElement;
